import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  clampVisualPage,
  visualPageAt,
  visualPageCount,
  visualPageLength,
} from './visual-pages.ts';

export interface VisualPageState {
  /** The sheet currently passing the reading position in the scroll pane. */
  current: number;
  /** Presentation-only sheets needed by the content at its current reflow. */
  total: number;
  /** One sheet's rendered block length in CSS pixels. Zero until layout exists. */
  pageLength: number;
}

interface VisualPages extends VisualPageState {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  paperRef: React.RefObject<HTMLElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  /** Recalculate after a value or view change; ResizeObserver handles reflow. */
  measure: () => void;
  /** Keep the position indicator in step with ordinary scrolling. */
  onScroll: () => void;
  /** Scroll, never replace or hide content. */
  jumpTo: (page: number) => void;
}

const FIRST_PAGE: VisualPageState = { current: 1, total: 1, pageLength: 0 };

function pixels(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** The paper's top edge in the scroll pane's own coordinate system. */
function paperTop(scroll: HTMLElement, paper: HTMLElement): number {
  return scroll.scrollTop + paper.getBoundingClientRect().top - scroll.getBoundingClientRect().top;
}

function samePages(a: VisualPageState, b: VisualPageState): boolean {
  return a.current === b.current && a.total === b.total && a.pageLength === b.pageLength;
}

/**
 * Measure and navigate visual pages without changing the note's data or DOM order.
 *
 * The content element, rather than the paper, owns the height measurement. The
 * paper is deliberately extended to a whole last sheet; measuring that extension
 * would make a note that shrank stay on its old page count forever.
 */
export function useVisualPages(prepareContent: () => void): VisualPages {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const paperRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [pages, setPages] = useState<VisualPageState>(FIRST_PAGE);
  const pagesRef = useRef<VisualPageState>(FIRST_PAGE);

  const commit = useCallback((next: VisualPageState) => {
    pagesRef.current = next;
    setPages((current) => (samePages(current, next) ? current : next));
  }, []);

  const currentFor = useCallback((pageLength: number, total: number): number => {
    const scroll = scrollRef.current;
    const paper = paperRef.current;
    if (scroll === null || paper === null) return clampVisualPage(pagesRef.current.current, total);
    const atBottom =
      scroll.scrollHeight > scroll.clientHeight &&
      scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - 1;
    if (atBottom) return total;
    const offset = scroll.scrollTop - paperTop(scroll, paper);
    return visualPageAt(offset, pageLength, total, scroll.clientHeight);
  }, []);

  const measure = useCallback(() => {
    prepareContent();
    const paper = paperRef.current;
    const content = contentRef.current;
    if (paper === null || content === null) return;

    const width = Math.max(paper.getBoundingClientRect().width, paper.clientWidth);
    const pageLength = visualPageLength(width);
    if (pageLength === 0) return;

    const contentHeight = Math.max(content.getBoundingClientRect().height, content.scrollHeight);
    const style = getComputedStyle(paper);
    const naturalHeight =
      contentHeight +
      pixels(style.paddingTop) +
      pixels(style.paddingBottom) +
      pixels(style.borderTopWidth) +
      pixels(style.borderBottomWidth);
    const total = visualPageCount(naturalHeight, pageLength);
    commit({ current: currentFor(pageLength, total), total, pageLength });
  }, [commit, currentFor, prepareContent]);

  const onScroll = useCallback(() => {
    const current = pagesRef.current;
    if (current.pageLength === 0) return;
    const nextPage = currentFor(current.pageLength, current.total);
    if (nextPage === current.current) return;
    commit({ ...current, current: nextPage });
  }, [commit, currentFor]);

  const jumpTo = useCallback(
    (requested: number) => {
      const scroll = scrollRef.current;
      const paper = paperRef.current;
      const current = pagesRef.current;
      if (scroll === null || paper === null || current.pageLength === 0) return;

      const page = clampVisualPage(requested, current.total);
      const top = Math.max(0, paperTop(scroll, paper) + (page - 1) * current.pageLength);
      const reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // jsdom and a few embedded webviews do not expose the method. Assignment
      // is the same navigation without animation and keeps the control useful.
      if (typeof scroll.scrollTo === 'function') {
        scroll.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
      } else {
        scroll.scrollTop = top;
      }
      commit({ ...current, current: page });
    },
    [commit],
  );

  useLayoutEffect(() => {
    measure();

    const paper = paperRef.current;
    const content = contentRef.current;
    const Observer = globalThis.ResizeObserver;
    const observer =
      Observer === undefined
        ? null
        : new Observer(() => {
            measure();
          });
    if (paper !== null) observer?.observe(paper);
    if (content !== null) observer?.observe(content);

    // ResizeObserver covers the paper itself. The window listener is the older
    // browser floor and also catches zoom implementations that report only a
    // viewport resize.
    window.addEventListener('resize', measure);

    let live = true;
    const fonts = document.fonts;
    const fontsSettled = () => {
      if (live) measure();
    };
    void fonts?.ready.then(fontsSettled);
    fonts?.addEventListener('loadingdone', fontsSettled);

    return () => {
      live = false;
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      fonts?.removeEventListener('loadingdone', fontsSettled);
    };
  }, [measure]);

  return { ...pages, scrollRef, paperRef, contentRef, measure, onScroll, jumpTo };
}
